import { LegalEntityCountry } from '@seapoint/server/enums';
import { ColumnDef } from '@tanstack/react-table';
import { User } from 'lucide-react';
import { Avatar } from '../../../../design-system/avatar/avatar/avatar';
import { Checkbox } from '../../../../design-system/checkbox/checkbox';
import { ColumnHeaderWithSorting } from '../../../../design-system/data-table/column-header-with-sorting';
import { DropdownMenu, DropdownMenuItem } from '../../../../design-system/dropdown-menu/dropdown-menu';
import { EmployeeView } from '../../../../types/entity-types';
import { getCountryFlag } from '../../../../utils/get-country-flag';
import { getBeneficiaryCountryDetails } from '../../../payments/utils/get-beneficiary-country-details';

export const employeesTableColumnDefinition: ColumnDef<EmployeeView>[] = [
  {
    id: 'select',
    header: ({ table }) => {
      const allIds = table.getRowModel().rows.map((row) => row.original.id);
      const selected = table.options.meta?.selectedRowIds ?? [];
      const allSelected = allIds.length > 0 && allIds.every((id) => selected.includes(id));

      return (
        <Checkbox
          checked={allSelected}
          onCheckedChange={(checked) => {
            if (checked) {
              table.options.meta?.setSelectedRowIds?.(Array.from(new Set([...selected, ...allIds])));
            } else {
              table.options.meta?.setSelectedRowIds?.(selected.filter((id) => !allIds.includes(id)));
            }
          }}
          aria-label="Select all"
        />
      );
    },
    cell: ({ row, table }) => (
      <Checkbox
        checked={table.options.meta?.selectedRowIds?.includes(row.original.id)}
        onCheckedChange={(checked) => {
          const id = row.original.id;
          const selected = table.options.meta?.selectedRowIds ?? [];
          if (checked) {
            table.options.meta?.setSelectedRowIds?.([...selected, id]);
          } else {
            table.options.meta?.setSelectedRowIds?.(selected.filter((sid) => sid !== id));
          }
        }}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
    size: 5,
    minSize: 5,
  },
  {
    id: 'avatar',
    header: () => null,
    cell: ({ row }) => {
      const employee = row.original;
      const fullName = `${employee.firstName} ${employee.lastName}`;
      return <Avatar name={fullName} size="sm" />;
    },
    enableSorting: false,
    enableHiding: false,
    size: 6,
    minSize: 6,
  },
  {
    accessorKey: 'firstName',
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="First Name" />,
    cell: ({ row }) => <span>{row.original.firstName}</span>,
    size: 18,
    minSize: 18,
  },
  {
    accessorKey: 'lastName',
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Last Name" />,
    cell: ({ row }) => <span>{row.original.lastName}</span>,
    size: 18,
    minSize: 18,
  },
  {
    accessorKey: 'employeeId',
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="ID" />,
    cell: ({ row }) => <span>{row.original.employeeId}</span>,
    size: 16,
    minSize: 16,
  },
  {
    accessorKey: 'role',
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Role" />,
    cell: ({ row }) => <span>{row.original.role || '-'}</span>,
    size: 22,
  },
  {
    accessorKey: 'country',
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Country" />,
    cell: ({ row }) => {
      const country = row.original.country;
      const flag = getCountryFlag(country as unknown as LegalEntityCountry);

      return (
        <div className="flex items-center gap-2">
          {flag && (
            <img
              src={flag}
              alt={country}
              loading="eager"
              fetchPriority="high"
              className="h-5 w-5 rounded-full object-cover"
            />
          )}
          <span>{getBeneficiaryCountryDetails(country).name}</span>
        </div>
      );
    },
    size: 16,
    minSize: 16,
  },
  {
    id: 'actions',
    cell: ({ row, table }) => {
      const employee = row.original;

      const menuItems: DropdownMenuItem[] = [
        {
          id: 'edit',
          label: 'Edit Employee',
          icon: <User size={16} />,
          onClick: () => table.options.meta?.onEditEmployee?.(employee.id),
        },
      ];

      return (
        <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
          <DropdownMenu items={menuItems} />
        </div>
      );
    },
    enableSorting: false,
    enableHiding: false,
    size: 10,
    minSize: 10,
  },
];
